import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { onboardingAPI } from "../services/api";
import { useMembership } from "./MembershipContext";

const OnboardingContext = createContext(null);

/**
 * The shop's first-run state: whether the wizard is due and what the
 * checklist shows. Owner only — everyone else sees neither. The server works
 * the checklist out from the shop's data, so this just asks again after
 * anything that could have changed it.
 */
export const OnboardingProvider = ({ children }) => {
  const { isOwner, membership, flag } = useMembership();
  const rolledOut = flag("onboarding");
  const [state, setState] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await onboardingAPI.get();
      setState(data);
      return data;
    } catch {
      // Not being able to read it only means nothing is shown
      return null;
    }
  }, []);

  useEffect(() => {
    // Not rolled out to this shop: the API would answer 404, and nothing is shown
    if (membership && isOwner && rolledOut) refresh();
  }, [membership, isOwner, rolledOut, refresh]);

  const update = useCallback(async (patch) => {
    const { data } = await onboardingAPI.update(patch);
    setState(data);
    return data;
  }, []);

  const markStep = useCallback(async (key) => {
    try {
      const { data } = await onboardingAPI.markStep(key);
      setState(data);
    } catch {
      // A step that cannot be marked is not worth interrupting anyone for
    }
  }, []);

  return (
    <OnboardingContext.Provider value={{ state, refresh, update, markStep }}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () =>
  useContext(OnboardingContext) || { state: null, refresh: async () => null, update: async () => null, markStep: async () => {} };
